/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { compact, isEmpty, noop } from 'lodash';

import { VisitDocumentFieldValue } from '@dreryk/egabinet-visit-frontend-api';

import { DocumentTemplatePreviewValues, TagActionConfig, TagActions } from './DocumentTemplateForm.types';

const isInput = (element: HTMLElement): element is HTMLInputElement => element.nodeName === 'INPUT';
const isCheckbox = (element: HTMLInputElement): boolean => element.type === 'checkbox';
const isTextarea = (element: HTMLElement): element is HTMLTextAreaElement => element.nodeName === 'TEXTAREA';
const isStrikethroughElement = (element: HTMLElement): boolean => element.classList.contains('strikethrough-wrapper');
const isOutlineElement = (element: HTMLElement): boolean => element.classList.contains('outline-wrapper');

const getDocumentTemplateElements = (editorElement: HTMLElement): HTMLElement[] => {
   return Array.from(
      editorElement.querySelectorAll<HTMLElement>('textarea, input, .strikethrough-wrapper, .outline-wrapper')
   );
};

let strikethroughIndex = 0;
let outlineIndex = 0;

const assignId = (element: HTMLElement, prefix: string, index: number): string => {
   const elementId = element.getAttribute('id') || `${prefix}-${index}`;
   element.setAttribute('id', elementId);
   return elementId;
};

export const extractDocumentTemplateValues = (editorElement: HTMLElement): DocumentTemplatePreviewValues => {
   return getDocumentTemplateElements(editorElement).reduce<DocumentTemplatePreviewValues>((result, element) => {
      if (isStrikethroughElement(element) || isOutlineElement(element)) {
         return result;
      }

      if (isTextarea(element)) {
         result[element.id] = element.value;
         return result;
      }

      if (isInput(element) && isCheckbox(element)) {
         result[element.id] = String(element.checked);
         return result;
      }

      if (isInput(element)) {
         result[element.id] = element.value;
      }

      return result;
   }, {});
};

const insertStrikethroughValue = (element: HTMLElement, value: string): void => {
   if (value === 'true' && !element.querySelector('s')) {
      element.innerHTML = `<s>${element.innerHTML}</s>`;
   } else if (value === 'false') {
      const sElement = element.querySelector('s');
      if (sElement) {
         element.innerHTML = sElement.innerHTML;
      }
   }
};

const insertOutlineValue = (element: HTMLElement, value: string): void => {
   if (value === 'true') {
      element.style.border = '1px solid #000';
      element.style.padding = '0 2px';
   } else if (value === 'false') {
      element.style.border = '';
      element.style.padding = '';
   }
};

export const insertDocumentTemplateValues = (
   editorElement: HTMLElement,
   documentTemplateDraft: DocumentTemplatePreviewValues
): void => {
   getDocumentTemplateElements(editorElement).forEach((element) => {
      if (!(element.id in documentTemplateDraft)) {
         return;
      }

      const value = documentTemplateDraft[element.id] ?? '';

      if (isStrikethroughElement(element)) {
         insertStrikethroughValue(element, value);
         return;
      }

      if (isOutlineElement(element)) {
         insertOutlineValue(element, value);
         return;
      }

      if (isTextarea(element)) {
         if (value) {
            element.textContent = value;
            element.value = value;
         }
         return;
      }

      if (isInput(element) && isCheckbox(element)) {
         element.checked = value === 'true';
         return;
      }

      if (isInput(element)) {
         element.setAttribute('value', value);
         element.value = value;
      }
   });
};

const ZERO_WIDTH_JOINER_PATTERN = /^\u200D$/u;

const appendTextareaValue = (textarea: HTMLTextAreaElement, value: string): string => {
   const currentValue = textarea.value.replace(ZERO_WIDTH_JOINER_PATTERN, '');
   return currentValue ? `${currentValue}, ${value}` : value;
};

const createActionButton = (document: Document, config: TagActionConfig, onClick: () => void): HTMLButtonElement => {
   const button = document.createElement('button');
   button.type = 'button';
   button.innerHTML = `${config.icon}<span>${config.label}</span>`;
   button.style.cssText = config.style;
   button.addEventListener('click', (event) => {
      event.preventDefault();
      onClick();
   });
   return button;
};

export const setDocumentTemplateElementsChangeEvent = (
   editorElement: HTMLElement,
   onChange: (id: string, value: string | undefined) => void,
   tagActions?: TagActions
): void => {
   strikethroughIndex = 0;
   outlineIndex = 0;

   getDocumentTemplateElements(editorElement).forEach((element) => {
      if (isStrikethroughElement(element)) {
         strikethroughIndex += 1;
         const elementId = assignId(element, 'strikethrough', strikethroughIndex);
         element.style.cursor = 'pointer';
         element.addEventListener('click', () => {
            const sElement = element.querySelector('s');
            if (sElement) {
               element.innerHTML = sElement.innerHTML;
               onChange(elementId, 'false');
            } else {
               element.innerHTML = `<s>${element.innerHTML}</s>`;
               onChange(elementId, 'true');
            }
         });
         return;
      }

      if (isOutlineElement(element)) {
         outlineIndex += 1;
         const elementId = assignId(element, 'outline', outlineIndex);
         element.style.cursor = 'pointer';
         element.addEventListener('click', () => {
            const hasBorder = element.style.border !== '';
            if (hasBorder) {
               element.style.border = '';
               element.style.padding = '';
               onChange(elementId, 'false');
            } else {
               element.style.border = '1px solid #000';
               element.style.padding = '0 2px';
               onChange(elementId, 'true');
            }
         });
         return;
      }

      if (isTextarea(element) && element.value) {
         onChange(element.id, element.value);
      }

      if (isTextarea(element) && tagActions) {
         const tagCode = element.getAttribute('data-tag-code');
         const actionConfig = tagCode ? tagActions[tagCode] : undefined;

         if (actionConfig) {
            const wrapper = editorElement.ownerDocument.createElement('div');
            wrapper.style.cssText = 'position: relative;';

            element.parentNode!.insertBefore(wrapper, element);
            wrapper.appendChild(element);

            const button = createActionButton(editorElement.ownerDocument, actionConfig, async () => {
               const value = await actionConfig.handler(element.id);

               if (value) {
                  const newValue = appendTextareaValue(element, value);
                  element.textContent = newValue;
                  element.value = newValue;
                  onChange(element.id, newValue);
                  element.dispatchEvent(new Event('input', { bubbles: true }));
               }
            });
            wrapper.appendChild(button);
         }
      }

      element.oninput = (event) => {
         const changedElement = event.target as HTMLElement;

         if (isInput(changedElement) && isCheckbox(changedElement)) {
            return onChange(changedElement.id, String(changedElement.checked));
         }

         if (isTextarea(changedElement)) {
            changedElement.textContent = changedElement.value;
            if (isEmpty(changedElement.value.trim())) {
               return onChange(changedElement.id, undefined);
            }
            return onChange(changedElement.id, changedElement.value);
         }

         if (isInput(changedElement)) {
            changedElement.setAttribute('value', changedElement.value);
            if (isEmpty(changedElement.value.trim())) {
               return onChange(changedElement.id, undefined);
            }
            return onChange(changedElement.id, changedElement.value);
         }

         return noop();
      };
   });
};

export const documentTemplateValuesMapToVisitDocumentFieldValue = (
   documentTemplateValues: DocumentTemplatePreviewValues
): VisitDocumentFieldValue[] => {
   return compact(
      Object.entries(documentTemplateValues).map(([name, value]): VisitDocumentFieldValue | null => {
         if (!value) {
            return null;
         }

         return {
            name: name,
            value: value,
         };
      })
   );
};
